import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShoppingBag, User, LogIn } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import StickyDiscountBar, { useDiscountBar } from "@/components/StickyDiscountBar";
import CartHoverPreview from "@/components/CartHoverPreview";
import logo from "@/assets/logo.webp";

const Navbar = () => {
 const [open, setOpen] = useState(false);
 const location = useLocation();
 const { t, lang, setLang, isAr } = useLanguage();
 const { totalItems } = useCart();
 const { user, loading } = useAuth();
 const { visible: discountBarVisible } = useDiscountBar();

 const navLinks = [
 { to: "/", label: t("nav.home") },
 { to: "/about", label: t("nav.about") },
 { to: "/shop", label: t("nav.shop") },
 { to: "/blog", label: t("nav.blog") },
 { to: "/wholesale", label: t("nav.wholesale") },
 { to: "/contact", label: t("nav.contact") },
 { to: "/sinai", label: "Sinai" },
 ];

 return (
 <>
 <header className="fixed left-0 right-0 top-0 z-50 bg-background/95 backdrop-blur-md shadow-[0_10px_30px_hsl(var(--foreground)/0.04)]">
 <nav className="container mx-auto flex items-center justify-between h-16 md:h-20 px-4">
 <Link to="/" className="flex items-center gap-2">
 <img src={logo} alt="Nil Beauty, Natural skincare and organic beauty products inspired by ancient Egyptian wisdom | منتجات عناية بالبشرة طبيعية" title="Nil Beauty" className="h-12 md:h-16 w-auto" width={64} height={48} />
 </Link>

 <div className="hidden md:flex items-center gap-8">
 <ul className="flex items-center gap-8">
 {navLinks.map((link) => {
 const isCurrent = location.pathname === link.to;
 return (
 <li key={link.to}>
 <Link
 to={link.to}
 aria-current={isCurrent ? "page" : undefined}
 className={`font-body text-sm tracking-widest uppercase transition-colors duration-300 hover:text-primary rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
 isCurrent ? "text-primary" : "text-foreground/70"
 }`}
 >
 {link.label}
 </Link>
 </li>
 );
 })}
 </ul>

 {!loading && (
 <Link
 to={user ? "/account" : "/login"}
 className="text-foreground/70 hover:text-primary transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
 aria-label={user ? (isAr ? "حسابي" : "My Account") : (isAr ? "تسجيل الدخول" : "Sign In")}
 title={user ? (isAr ? "حسابي" : "My Account") : (isAr ? "تسجيل الدخول" : "Sign In")}
 >
 {user ? <User size={20}  aria-hidden="true" focusable="false" /> : <LogIn size={20}  aria-hidden="true" focusable="false" />}
 </Link>
 )}

 <div className="relative group">
 <Link to="/cart" className="relative text-foreground/70 hover:text-primary transition-colors inline-flex" aria-label="Shopping cart">
 <ShoppingBag id="navbar-cart-icon" size={22}  aria-hidden="true" focusable="false" />
 {totalItems > 0 && (
 <span className="absolute -top-2 -end-2 w-5 h-5 rounded-full gold-gradient text-[10px] font-body font-bold flex items-center justify-center text-foreground">
 {totalItems}
 </span>
 )}
 </Link>
 <CartHoverPreview />
 </div>

 <div className="flex items-center border border-primary/30 rounded-sm overflow-hidden" role="group" aria-label="Language switcher">
 <button
 type="button"
 onClick={() => setLang("en")}
 aria-label="Switch to English"
 aria-pressed={lang === "en"}
 className={`px-3 py-1.5 font-body text-xs tracking-wider transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
 lang === "en" ? "gold-gradient text-foreground" : "text-muted-foreground hover:text-primary"
 }`}
 >
 EN
 </button>
 <div className="w-px h-5 bg-primary/20" />
 <button
 type="button"
 onClick={() => setLang("ar")}
 aria-label="التبديل إلى العربية"
 aria-pressed={lang === "ar"}
 className={`px-3 py-1.5 font-body text-xs tracking-wider transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
 lang === "ar" ? "gold-gradient text-foreground" : "text-muted-foreground hover:text-primary"
 }`}
 >
 AR
 </button>
 </div>
 </div>

 <div className="flex items-center gap-3 md:hidden">
 {!loading && (
 <Link
 to={user ? "/account" : "/login"}
 className="text-foreground/70 hover:text-primary transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
 aria-label={user ? (isAr ? "حسابي" : "My Account") : (isAr ? "تسجيل الدخول" : "Sign In")}
 >
 {user ? <User size={18}  aria-hidden="true" focusable="false" /> : <LogIn size={18}  aria-hidden="true" focusable="false" />}
 </Link>
 )}
 <Link to="/cart" className="relative text-foreground/70 hover:text-primary transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background" aria-label="Shopping cart">
 <ShoppingBag size={20}  aria-hidden="true" focusable="false" />
 {totalItems > 0 && (
 <span className="absolute -top-2 -end-2 w-5 h-5 rounded-full gold-gradient text-[10px] font-body font-bold flex items-center justify-center text-foreground">
 {totalItems}
 </span>
 )}
 </Link>
 <button
 type="button"
 onClick={() => setLang(lang === "en" ? "ar" : "en")}
 aria-label={lang === "en" ? "Switch language to Arabic" : "Switch language to English"}
 className="font-body text-xs tracking-wider text-primary border border-primary/30 px-2 py-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
 >
 {lang === "en" ? "AR" : "EN"}
 </button>
 <button
 type="button"
 onClick={() => setOpen(!open)}
 className="text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
 aria-label={open ? (isAr ? "إغلاق القائمة" : "Close menu") : (isAr ? "فتح القائمة" : "Open menu")}
 aria-expanded={open}
 aria-controls="mobile-nav"
 >
 {open ? <X size={24}  aria-hidden="true" focusable="false" /> : <Menu size={24}  aria-hidden="true" focusable="false" />}
 </button>
 </div>
 </nav>

 {discountBarVisible && (
 <div className="border-t border-primary/10">
 <div className="container mx-auto px-4">
 <StickyDiscountBar />
 </div>
 </div>
 )}

 {open && (
 <div id="mobile-nav" className="md:hidden bg-background border-t border-primary/10 animate-fade-up">
 <ul className="flex flex-col items-center py-6 gap-6">
 {navLinks.map((link) => {
 const isCurrent = location.pathname === link.to;
 return (
 <li key={link.to}>
 <Link
 to={link.to}
 onClick={() => setOpen(false)}
 aria-current={isCurrent ? "page" : undefined}
 className={`font-body text-sm tracking-widest uppercase transition-colors duration-300 hover:text-primary rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
 isCurrent ? "text-primary" : "text-foreground/70"
 }`}
 >
 {link.label}
 </Link>
 </li>
 );
 })}
 </ul>
 </div>
 )}
 </header>
 {discountBarVisible && <div aria-hidden="true" className="h-10" />}
 </>
 );
};

export default Navbar;